import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Title,
  Group,
  Button,
  Chip,
  Menu,
  ActionIcon,
} from "@mantine/core";
import {
  IconClockX,
  IconDotsVertical,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { AppointmentForm } from "../../../../../types/appointmentForms";
import { FormCategory, FormTemplate } from "../../../../../types/formTemplates";
import { useAppointmentForms } from "../../../../../hooks/useAppointmentForms";
import DeleteConfirmationModal from "../../../../common/DeleteConfirmationModal";
import { TemplateLibraryModal } from "../../../../formTemplates/TemplateLibraryModal";
import { FormViewer } from "../../../../formTemplates/viewer/FormViewer";
import { ClientFormsListSection } from "../../../../formTemplates/ClientFormsListSection";

interface AppointmentFormsPanelProps {
  transactionUuid: string;
  salonUuid?: string;
  clientUuid?: string;
  canEdit?: boolean;
  /** Seed forms from the parent transaction (GetTransactions embeds them) to
   *  skip a redundant fetch. Omit to fetch fresh from the forms endpoint. */
  initialForms?: AppointmentForm[] | null;
  /** Reports the current form count up to the parent (e.g. for a tab badge). */
  onCountChange?: (count: number) => void;
  /** Reports the count of forms still owed by client/staff (status not
   *  "completed" or "expired"). Used by sibling panels to decide whether to
   *  surface a "send forms reminder" affordance. */
  onPendingCountChange?: (count: number) => void;
  /** Reports the full forms list after add/remove/edit so the parent can keep
   *  the calendar cache + event popover in sync. */
  onFormsChange?: (forms: AppointmentForm[]) => void;
}

export default function AppointmentFormsPanel({
  transactionUuid,
  salonUuid,
  clientUuid,
  canEdit = true,
  initialForms,
  onCountChange,
  onPendingCountChange,
  onFormsChange,
}: AppointmentFormsPanelProps) {
  const {
    summaryForms,
    formByUuid,
    loading,
    assigning,
    assign,
    remove,
    expire,
    refetch,
    totalCount,
    pendingCount,
  } = useAppointmentForms(transactionUuid, {
    initialForms,
    salonUuid,
    clientUuid,
    onFormsChange,
  });

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AppointmentForm | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [expireTarget, setExpireTarget] = useState<AppointmentForm | null>(
    null,
  );
  const [expiring, setExpiring] = useState(false);
  // Form selected for inline view/fillout. FormViewer mounts as a Drawer/Modal
  // and reads/writes the form's data via its own AppContext API hooks; we
  // refetch the list on close so submission state is reflected.
  const [viewerForm, setViewerForm] = useState<AppointmentForm | null>(null);

  type CategoryFilter = "all" | FormCategory;
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all");

  const visibleSummaryForms = useMemo(() => {
    if (categoryFilter === "all") return summaryForms;
    return summaryForms.filter((f) => f.category === categoryFilter);
  }, [summaryForms, categoryFilter]);

  const distinctCategories = useMemo(() => {
    return new Set(summaryForms.map((f) => f.category));
  }, [summaryForms]);
  const showCategoryFilter = distinctCategories.size > 1;

  useEffect(() => {
    onCountChange?.(totalCount);
  }, [totalCount, onCountChange]);

  useEffect(() => {
    onPendingCountChange?.(pendingCount);
  }, [pendingCount, onPendingCountChange]);

  const handleUseTemplate = async (template: FormTemplate) => {
    const ok = await assign(template);
    if (ok) setLibraryOpen(false);
  };

  // Edit / Create-new are handled on the client-details page only — appointment
  // view is read+assign. canManageTemplates={false} hides those actions in the
  // modal, but the props are still required so we pass close-only stubs.
  const handleEditCustom = () => setLibraryOpen(false);
  const handleCreateNew = () => setLibraryOpen(false);

  const handleRemoveConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const ok = await remove(deleteTarget);
    setDeleting(false);
    if (ok) setDeleteTarget(null);
  };

  const handleExpireConfirm = async () => {
    if (!expireTarget) return;
    setExpiring(true);
    const ok = await expire(expireTarget);
    setExpiring(false);
    if (ok) setExpireTarget(null);
  };

  const renderBody = () => (
    <ClientFormsListSection
      forms={visibleSummaryForms}
      isLoading={loading}
      density="compact"
      showStatusFilter={false}
      emptyTitle={
        categoryFilter === "all"
          ? "No forms assigned"
          : "No forms match this filter"
      }
      emptyDescription={
        categoryFilter !== "all"
          ? "Switch the filter back to 'All' to see other categories."
          : canEdit
            ? "Click + Assign Form to add an intake, waiver, or note."
            : "Forms required by this appointment's services will appear here."
      }
      onOpenForm={(summary) => {
        const original = formByUuid.get(summary.id);
        if (original) setViewerForm(original);
      }}
      renderRowActions={(summary) => {
        const original = formByUuid.get(summary.id);
        if (!original || !canEdit) return null;
        const isDraft = original.state
          ? original.state === "draft"
          : !original.is_submitted;
        const alreadyExpired = original.status === "expired";
        const canExpire =
          !isDraft && !alreadyExpired && !!clientUuid && !!salonUuid;
        if (!isDraft && !canExpire) return null;
        return (
          <Menu shadow="md" position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {isDraft && (
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => setDeleteTarget(original)}
                >
                  Remove
                </Menu.Item>
              )}
              {canExpire && (
                <Menu.Item
                  leftSection={<IconClockX size={14} />}
                  onClick={() => setExpireTarget(original)}
                >
                  Set expire date
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        );
      }}
    />
  );

  const countLabel = loading || totalCount === 0 ? "" : ` (${totalCount})`;

  return (
    <>
      <Box>
        <Group justify="space-between" mb="md">
          <Title order={4}>Forms{countLabel}</Title>
          {canEdit && (
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconPlus size={16} />}
              onClick={() => setLibraryOpen(true)}
            >
              Assign Form
            </Button>
          )}
        </Group>
        {showCategoryFilter && (
          <Chip.Group
            multiple={false}
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v as CategoryFilter)}
          >
            <Group gap="xs" mb="sm">
              <Chip value="all" size="xs" variant="light">
                All ({summaryForms.length})
              </Chip>
              {distinctCategories.has("reusable") && (
                <Chip value="reusable" size="xs" variant="light">
                  Reusable
                </Chip>
              )}
              {distinctCategories.has("per_visit") && (
                <Chip value="per_visit" size="xs" variant="light">
                  Per-visit
                </Chip>
              )}
              {distinctCategories.has("per_visit_clinical") && (
                <Chip value="per_visit_clinical" size="xs" variant="light">
                  SOAP
                </Chip>
              )}
              {distinctCategories.has("post_appointment") && (
                <Chip value="post_appointment" size="xs" variant="light">
                  Post-Appointment
                </Chip>
              )}
            </Group>
          </Chip.Group>
        )}
        {renderBody()}
      </Box>

      <DeleteConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleRemoveConfirm}
        title="Remove Form"
        message={`Remove "${deleteTarget?.name}" from this appointment? This cannot be undone.`}
        isLoading={deleting}
      />

      <DeleteConfirmationModal
        isOpen={!!expireTarget}
        onClose={() => setExpireTarget(null)}
        onConfirm={handleExpireConfirm}
        title="Expire Form"
        message={`Expire "${expireTarget?.name}"? This invalidates the form (e.g. when a submission turns out to be wrong). The record stays in the audit trail. To collect fresh data, assign the form again.`}
        confirmLabel="Expire"
        isLoading={expiring}
      />

      <TemplateLibraryModal
        opened={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onUseTemplate={handleUseTemplate}
        onEditCustom={handleEditCustom}
        onCreateNew={handleCreateNew}
        canManageTemplates={false}
        canManageClientForms={canEdit}
        isAssigning={assigning}
      />

      {viewerForm && salonUuid && clientUuid && (
        <FormViewer
          salonId={salonUuid}
          clientId={clientUuid}
          formId={viewerForm.uuid}
          onClose={() => {
            setViewerForm(null);
            // Refetch on close so status changes that don't go through
            // onChanged are reflected — chiefly the fire-and-forget
            // mark-opened that flips a "Not started" form to "Opened" on
            // first view (and any other async server-side update).
            void refetch();
          }}
          onChanged={refetch}
        />
      )}
    </>
  );
}
